/**
 * Orders list (web) — Phase 5 marketplace.
 *
 * Lists the orders the signed-in caller is party to (GET /orders — the server
 * returns rows where the caller is either the buyer OR the seller). Each row
 * shows the counterparty (whichever side the caller is NOT), the product, qty,
 * the order total, and the status; the row links to /orders/:id for detail +
 * settle / cancel actions.
 *
 * Counterparty resolution: we read the caller's `sub` from the access token and
 * compare against `buyerUserId` — if the caller is the buyer we show the seller
 * name, otherwise the buyer name. The seller name can be null (an order minted
 * from a coop listing carries a coop name), so we fall back to the coop name
 * then a dash.
 *
 * MONEY: totalZarpMicro is a micro-ZARP (1e-6 ZARP) STRING — render via
 * microToZarp() + <Money>, never Number() for math. The list endpoint returns a
 * {data:[]} envelope (unwrapped in src/api/orders.ts → we read `.data`).
 *
 * Role: any party (buyer + sellers). No hard role gate — the server scopes the
 * list to the caller.
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Money,
  Skeleton,
  Stack,
  StatusPill,
  Text,
  type StatusPillVariant,
} from '@fse/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { FseApiError, readAccessTokenClaims } from '../api/client';
import { listOrders, type OrderDto, type OrderStatus } from '../api/orders';
import { microToZarp } from '../api/market';

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

// StatusPill variants per order status (semantic names). Covers the full
// settlement lifecycle the API exposes (escrowed..refunded land with TD-126);
// the off-platform MVP only ever reaches draft/settled/cancelled today.
const ORDER_PILL: Record<OrderStatus, StatusPillVariant> = {
  draft: 'due',
  escrowed: 'active',
  delivered: 'active',
  attested: 'active',
  settled: 'sold',
  disputed: 'overdue',
  refunded: 'overdue',
  cancelled: 'overdue',
};

function errMsg(err: unknown): string {
  return err instanceof FseApiError ? err.message : String(err);
}

export function OrdersScreen() {
  const { t } = useTranslation();
  const callerSub = readAccessTokenClaims()?.sub ?? null;

  const ordersQuery = useQuery({
    queryKey: ['orders'],
    queryFn: ({ signal }) => listOrders({}, signal),
  });

  const orders = ordersQuery.data?.data ?? [];

  return (
    <Stack spacing={3}>
      <Box>
        <Text variant="headline-medium" as="h2">
          {t('ordersList.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {ordersQuery.data
              ? t('ordersList.subtitleWithCount', { count: orders.length })
              : t('ordersList.subtitle')}
          </Text>
        </Box>
      </Box>

      {ordersQuery.isPending ? (
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={96} />
          <Skeleton variant="rectangular" height={96} />
        </Stack>
      ) : ordersQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => ordersQuery.refetch()}>
              {t('ordersList.retry')}
            </Button>
          }
        >
          {t('ordersList.loadError', { error: errMsg(ordersQuery.error) })}
        </Alert>
      ) : orders.length === 0 ? (
        <Card>
          <CardContent>
            <Text variant="title-medium" as="h3">
              {t('ordersList.empty.title')}
            </Text>
            <Box sx={{ mt: 1 }}>
              <Text variant="body-medium" color="text.secondary">
                {t('ordersList.empty.body')}
              </Text>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {orders.map((order) => (
            <OrderRow key={order.id} order={order} callerSub={callerSub} t={t} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

interface OrderRowProps {
  order: OrderDto;
  callerSub: string | null;
  t: TFunction;
}

/** Resolve the name of the party the caller is NOT (their counterparty). */
function counterpartyName(order: OrderDto, callerSub: string | null): string {
  const callerIsBuyer = callerSub !== null && order.buyerUserId === callerSub;
  if (callerIsBuyer) {
    return order.sellerName ?? order.sellerCoopName ?? '—';
  }
  return order.buyerName ?? '—';
}

function OrderRow({ order, callerSub, t }: OrderRowProps) {
  const total = microToZarp(order.totalZarpMicro);
  const callerIsBuyer = callerSub !== null && order.buyerUserId === callerSub;
  const counterparty = counterpartyName(order, callerSub);

  return (
    <Link to="/orders/$id" params={{ id: order.id }} style={{ textDecoration: 'none' }}>
      <Card
        variant="outlined"
        sx={{ cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
      >
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Text variant="title-medium" as="span">
                  {order.product ?? t('ordersList.unknownProduct')}
                </Text>
                <StatusPill variant={ORDER_PILL[order.status]}>
                  {t(`ordersList.status.${order.status}`)}
                </StatusPill>
              </Stack>
              <Text variant="body-medium" color="text.secondary">
                {callerIsBuyer
                  ? t('ordersList.fromSeller', { name: counterparty })
                  : t('ordersList.toBuyer', { name: counterparty })}
                {' · '}
                {t('ordersList.qty', { qty: order.qtyKg })}
                {' · '}
                {dateFormatter.format(new Date(order.createdAt))}
              </Text>
            </Stack>
            <Box sx={{ textAlign: 'right' }}>
              <Text variant="label-medium" color="text.secondary">
                {t('ordersList.total')}
              </Text>
              <Box sx={{ mt: 0.25 }}>
                <Money amount={total} size="md" />
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Link>
  );
}
